package com.eladfinish.bible.contest

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.navigation.ExperimentalBrowserHistoryApi
import androidx.navigation.NavBackStackEntry
import androidx.navigation.NavController
import androidx.navigation.NavGraph
import androidx.navigation.bindToBrowserNavigation
import androidx.navigation.compose.rememberNavController
import androidx.navigation.toRoute
import com.eladfinish.bible.contest.data.common.WebFileStorage
import com.eladfinish.bible.contest.features.deeplink.ScreenSlugs
import com.eladfinish.bible.contest.navigation.RootNavigationDestination
import com.eladfinish.bible.contest.navigation.SearchScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.ShonotScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.back.PlatformBackInterception
import com.eladfinish.bible.contest.navigation.toScreenKeyOrNull
import io.github.aakira.napier.Napier
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.w3c.dom.events.Event

private object WebAppActions : AppActions {
    // A page "restarts" by reloading: startup then re-reads the saved language and font. The URL
    // fragment survives the reload, so the user lands back on the screen they were on.
    override fun restartApp() = window.location.reload()

    // Settings has already cleared the preferences (which live in localStorage) by the time it
    // calls this; what is left of the app's own data is its stored files and the tab's session.
    override fun clearApplicationData() {
        WebFileStorage().deleteAll()
        window.sessionStorage.clear()
    }

    // Everything below has no meaning for a website, and the app never asks for it on web: the
    // menu items and buttons leading to these are hidden there (the isWeb / isAndroid /
    // shouldShowAds / canPurchaseAdFree gates in MainMenu.kt and around it). A page can't close
    // its own tab; it is always the current version, so there is nothing to update to and no
    // store listing to open or rate; and it has no ads SDK and no in-app purchases. The
    // long-click actions are debug shortcuts on Android.
    override fun exitApp() {}
    override fun checkUpdate() {}
    override fun rateApp() {}
    override fun openAppStore() {}
    override fun upgrade() {}
    override fun showAd() {}
    override fun showRewardedAd() {}
    override fun startRemoveAdsPurchase() {}
    override fun restorePurchase() {}
    override fun consumeTestPurchase() {}
    override fun onMikudLongClicked() {}
    override fun onSettingsLongClicked() {}
    override fun onBrowseLongClicked() {}
    override fun onAboutLongClicked() {}
}

@OptIn(ExperimentalBrowserHistoryApi::class)
@Composable
fun RootContentJs() {
    val navController = rememberNavController()

    // Binds the browser's address bar + Back/Forward buttons to the NavController: every
    // navigate()/popBackStack() pushes a browser history entry (so Back actually works), and the
    // URL fragment shows a distinct path per screen.
    LaunchedEffect(navController) {
        // bindToBrowserNavigation only restores the initial URL fragment when we don't pass a
        // custom getBackStackEntryRoute (see androidx BrowserHistory.kt), which we do for the
        // slug mapping below. So without this, opening e.g. "#names" (or refreshing any inner
        // screen) starts at the default RootNavigationDestination and immediately overwrites the
        // fragment to "#root". Restore it ourselves before binding.
        restoreDestinationFromUrlFragment(navController, isInitialLoad = true)

        navController.bindToBrowserNavigation { entry: NavBackStackEntry -> entry.toUrlFragment() }
    }

    // The same gap bindToBrowserNavigation has for the initial load also applies to a page
    // that's already open: editing the URL fragment in the address bar and pressing Enter is a
    // same-document navigation (no reload) that fires "popstate" with event.state == null —
    // androidx's own popstate handler would restore from it, but only when there's no custom
    // getBackStackEntryRoute (see BrowserHistory.kt), which we need for our slug/query URLs. So
    // watch for it ourselves too: the listener only records the new fragment (plain state write,
    // no coroutine), and a LaunchedEffect below reacts to it. A coroutine launched directly from
    // this raw DOM listener (outside any Compose effect) was observed to hang forever inside
    // delay() / suspend across a navigate() call — LaunchedEffect uses Compose's own coroutine
    // context, which is what the initial-load restore above already relies on successfully.
    BrowserBackInterception(navController)

    var popstateFragment by remember { mutableStateOf<String?>(null) }
    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = { event ->
            if (event.asDynamic().state == null) {
                popstateFragment = window.location.hash
            }
        }
        window.addEventListener("popstate", listener)
        onDispose { window.removeEventListener("popstate", listener) }
    }
    LaunchedEffect(popstateFragment) {
        if (popstateFragment != null) {
            restoreDestinationFromUrlFragment(navController, isInitialLoad = false)
        }
    }

    CompositionLocalProvider(LocalAppActions provides WebAppActions) {
        BibleContestApp(
            navController = navController,
            appActions = WebAppActions,
            isDebug = false,
            logEvent = {},
            logEventWithParameter = { _, _, _ -> },
            onHebrewGenderConfirmed = {},
            versionInfo = "web",
            onBackClick = {},
            statusBar = {},
        )
    }
}

/**
 * Gives the visible screen its say on the browser's Back button, the way MyBackHandler does for
 * the system Back on Android: a screen that intercepts Back (InterceptBackWhen — e.g. Mikud,
 * which saves the selection before leaving) would otherwise be skipped, because the browser has
 * already moved to the previous history entry by the time the page hears about it ("popstate"),
 * and bindToBrowserNavigation then pops the screen to match.
 *
 * So this listens first (it is registered before bindToBrowserNavigation's own listener, and in
 * the capture phase). When the browser stepped back and the screen consumes it, the event is
 * kept from bindToBrowserNavigation and the browser is moved forward again to the entry it left,
 * leaving history and app in agreement; whatever the screen then does (show a dialog, navigate
 * back itself) proceeds as it would from the toolbar's Back.
 */
@Composable
private fun BrowserBackInterception(navController: NavController) {
    DisposableEffect(navController) {
        // The "popstate" our own history.forward() below is about to cause.
        var isRestoringEntry = false

        val listener: (Event) -> Unit = listener@{ event ->
            if (isRestoringEntry) {
                isRestoringEntry = false
                event.stopImmediatePropagation()
                return@listener
            }

            // bindToBrowserNavigation stores each entry's back stack as its state, one route per
            // line; no state means a hand-edited URL, which is not a Back press.
            val targetRoutes = (event.asDynamic().state as? String)?.lines() ?: return@listener
            val currentDepth = navController.currentBackStack.value.count { it.destination !is NavGraph }
            val isStepBack = targetRoutes.size < currentDepth
            if (!isStepBack) return@listener

            if (PlatformBackInterception.interceptBack?.invoke() == true) {
                event.stopImmediatePropagation()
                isRestoringEntry = true
                window.history.forward()
            }
        }
        window.addEventListener("popstate", listener, true)
        onDispose { window.removeEventListener("popstate", listener, true) }
    }
}

/**
 * The URL fragment for a back stack entry: "#slug", plus "?query" when [ScreenSlugs.queryFor] has
 * something to preserve for this screen (see its doc for how to add one).
 */
private fun NavBackStackEntry.toUrlFragment(): String {
    val screenKey = toScreenKeyOrNull() ?: destination.route.orEmpty()
    val slug = ScreenSlugs.slugOf(screenKey)
    val query = urlQueryFor(slug, this)
    return if (query != null) "#$slug?$query" else "#$slug"
}

/** Reads the entry's actual route for the screens whose [ScreenSlugs.queryFor] keeps some state. */
private fun urlQueryFor(slug: String, entry: NavBackStackEntry): String? = when (slug) {
    ScreenSlugs.SEARCH -> ScreenSlugs.queryFor(entry.toRoute<SearchScreenNavigationDestination>())
    ScreenSlugs.SHONOT -> ScreenSlugs.queryFor(entry.toRoute<ShonotScreenNavigationDestination>())
    else -> null
}

// NavHost attaches its graph to the NavController as part of BibleContestApp's own composition,
// which hasn't necessarily committed yet on the very first frame the app's LaunchedEffect runs
// in — navigate() throws IllegalStateException ("You must call setGraph() before calling
// getGraph()") until it has. currentBackStackEntry is null (not throwing) until the graph is
// attached, so on [isInitialLoad] poll that instead of guessing at a fixed number of retries. A
// later call (from the popstate-triggered LaunchedEffect, once the page is already up and
// running) skips that wait — the graph is certainly attached by then.
//
// [isInitialLoad] also decides whether to skip navigating to Root: avoids a redundant duplicate
// entry on the very first call, since the NavHost already starts there — but the popstate case
// (e.g. editing the address bar to "#root" while on another screen) should still navigate.
private suspend fun restoreDestinationFromUrlFragment(navController: NavController, isInitialLoad: Boolean) {
    val fragment = window.location.hash.removePrefix("#")
    val slug = fragment.substringBefore('?')
    val query = fragment.substringAfter('?', missingDelimiterValue = "")
    val destination = ScreenSlugs.destinationFor(slug, query) ?: return
    if (isInitialLoad && destination == RootNavigationDestination) return

    if (isInitialLoad) {
        var waitedMs = 0
        while (navController.currentBackStackEntry == null && waitedMs < 10_000) {
            delay(16)
            waitedMs += 16
        }
    }

    try {
        navController.navigate(destination)
    } catch (e: IllegalStateException) {
        Napier.w(tag = "RootContentJs", message = "Can't restore destination for URL fragment '#$slug'", throwable = e)
    } catch (e: IllegalArgumentException) {
        Napier.w(tag = "RootContentJs", message = "Can't restore destination for URL fragment '#$slug'", throwable = e)
    }
}
